import {
  useCreateExternalMcpMutation,
  useDeleteExternalMcpMutation,
  useUpdateExternalMcpMutation,
} from 'lauria/api/externalMcp'
import {
  createExternalMcpLocalId,
  mapExternalMcpToPromptItem,
} from 'lauria/pages/prompts/mappers/externalMcpMapper'
import {
  ExternalMcpFormValues,
  IExternalMcp,
  PromptExternalMcpItem,
} from 'lauria/types/externalMcp'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type UsePromptExternalMcpsParams = {
  promptId?: number
  enabled: boolean
  promptLoaded: boolean
  initialExternalMcps?: IExternalMcp[]
}

export const usePromptExternalMcps = ({
  promptId,
  enabled,
  promptLoaded,
  initialExternalMcps = [],
}: UsePromptExternalMcpsParams) => {
  const [items, setItems] = useState<PromptExternalMcpItem[]>([])
  const [isSyncing, setIsSyncing] = useState(false)
  const initializedPromptIdRef = useRef<number | null>(null)

  const [createExternalMcp] = useCreateExternalMcpMutation()
  const [updateExternalMcp] = useUpdateExternalMcpMutation()
  const [deleteExternalMcp] = useDeleteExternalMcpMutation()

  useEffect(() => {
    initializedPromptIdRef.current = null
    setItems([])
  }, [promptId])

  useEffect(() => {
    if (!enabled || !promptId || !promptLoaded) {
      return
    }

    if (initializedPromptIdRef.current === promptId) {
      return
    }

    initializedPromptIdRef.current = promptId
    setItems(initialExternalMcps.map(mapExternalMcpToPromptItem))
  }, [enabled, initialExternalMcps, promptId, promptLoaded])

  const visibleItems = useMemo(
    () => items.filter((item) => item.syncStatus !== 'pending_delete'),
    [items]
  )

  const addItem = useCallback((values: ExternalMcpFormValues) => {
    setItems((previousItems) => [
      ...previousItems,
      {
        localId: createExternalMcpLocalId(),
        authMethod: values.authMethod,
        url: values.url,
        secret: values.secret,
        syncStatus: 'pending_create',
      },
    ])
  }, [])

  const updateItem = useCallback(
    (
      localId: string,
      values: Pick<ExternalMcpFormValues, 'authMethod' | 'url'>
    ) => {
      setItems((previousItems) =>
        previousItems.map((item) => {
          if (item.localId !== localId) {
            return item
          }

          const nextItem: PromptExternalMcpItem = {
            ...item,
            authMethod: values.authMethod,
            url: values.url,
          }

          if (item.syncStatus === 'pending_create') {
            return nextItem
          }

          return {
            ...nextItem,
            syncStatus: 'pending_update',
          }
        })
      )
    },
    []
  )

  const markItemForDeletion = useCallback((localId: string) => {
    setItems((previousItems) => {
      const targetItem = previousItems.find((item) => item.localId === localId)

      if (!targetItem) {
        return previousItems
      }

      if (targetItem.syncStatus === 'pending_create') {
        return previousItems.filter((item) => item.localId !== localId)
      }

      return previousItems.map((item) =>
        item.localId === localId
          ? { ...item, syncStatus: 'pending_delete' as const }
          : item
      )
    })
  }, [])

  const syncExternalMcps = useCallback(
    async (targetPromptId: number) => {
      if (!enabled) {
        return
      }

      setIsSyncing(true)

      try {
        const itemsToDelete = items.filter(
          (item) => item.syncStatus === 'pending_delete' && item.serverId
        )
        const itemsToUpdate = items.filter(
          (item) => item.syncStatus === 'pending_update' && item.serverId
        )
        const itemsToCreate = items.filter(
          (item) => item.syncStatus === 'pending_create'
        )

        for (const item of itemsToDelete) {
          await deleteExternalMcp({
            promptId: targetPromptId,
            idExternalMcp: item.serverId as number,
          }).unwrap()
        }

        for (const item of itemsToUpdate) {
          await updateExternalMcp({
            promptId: targetPromptId,
            idExternalMcp: item.serverId as number,
            body: {
              authMethod: item.authMethod,
              url: item.url,
            },
          }).unwrap()
        }

        for (const item of itemsToCreate) {
          await createExternalMcp({
            promptId: targetPromptId,
            body: {
              authMethod: item.authMethod,
              url: item.url,
              secret: item.secret ?? '',
            },
          }).unwrap()
        }
      } finally {
        setIsSyncing(false)
      }
    },
    [createExternalMcp, deleteExternalMcp, enabled, items, updateExternalMcp]
  )

  return {
    visibleItems,
    isSyncing,
    addItem,
    updateItem,
    markItemForDeletion,
    syncExternalMcps,
  }
}
